/** Final App router — wires every public catalog, detail, cart, checkout, dashboards. */
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { AuthProvider, useAuth } from "./features/auth/AuthContext";
import { CartProvider } from "./features/cart/CartContext";
import { ToastProvider, TooltipProvider } from "./components/ui";
import { SiteLayout } from "./components/layout/SiteLayout";
import { ScrollToTop } from "./lib/ScrollToTop";
import { api } from "./lib/api";

import { HomePage } from "./pages/HomePage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { VideosPage } from "./features/videos/Videos";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { MaintenancePage } from "./pages/MaintenancePage";
import { PrivacyPolicyPage, TermsConditionsPage, RefundPolicyPage, AccessibilityPage } from "./pages/LegalPage";

import { LoginPage } from "./features/auth/LoginPage";
import { RegisterPage } from "./features/auth/RegisterPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./features/auth/ForgotPassword";
import { RequireAuth, RequireAdmin } from "./features/auth/guards";

import { CatalogPage } from "./features/catalog/CatalogPage";
import { DetailPage } from "./features/catalog/DetailPage";
import { ReaderPage } from "./features/catalog/ReaderPage";
import { CartPage, CheckoutPage, OrderSuccessPage, BookingPage } from "./features/cart/CartPages";

import { StudentDashboard } from "./features/dashboard/StudentDashboard";
import { AdminConsole } from "./features/dashboard/AdminConsole";

// Handles the `#session_id=...` fragment returned by Emergent Google OAuth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function OAuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    handled.current = true;
    const sessionId = decodeURIComponent(m[1]);
    // Clear the fragment immediately so refresh doesn't re-trigger
    window.history.replaceState({}, "", window.location.pathname + window.location.search);
    (async () => {
      try {
        const r = await api.post("/auth/google/session", { session_id: sessionId });
        if (refresh) await refresh();  // pull /auth/me into AuthContext
        const dest = r.data?.user?.role === "admin" || r.data?.user?.role === "super_admin" ? "/admin" : "/dashboard";
        nav(dest, { replace: true });
      } catch (e) {
        console.error("Google auth failed:", e);
        nav("/login?error=google", { replace: true });
      }
    })();
  }, [location.hash, nav, refresh]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <CurrencyProvider>
        <AuthProvider>
          <CartProvider>
            <TooltipProvider delayDuration={200}>
              <ToastProvider>
                <ScrollToTop />
                <OAuthCallback />
                <Routes>
                  <Route element={<SiteLayout />}>
                    <Route index element={<HomePage />} />

                    <Route path="/courses" element={<CatalogPage title="Premium Courses" eyebrow="Lineage-verified curriculum" lede="A complete catalog of courses across ten classical traditions — taught with rigour, supported by texts, and certified on completion." kind="course" listPath="/courses" categoriesPath="/courses/categories" detailPath="/courses/:slug" showLevel showRating />} />
                    <Route path="/courses/:slug" element={<DetailPage kind="course" listPath="/courses" detailPath="/courses/:slug" listLabel="Courses" />} />

                    <Route path="/sadhana" element={<CatalogPage title="Authentic Sadhana" eyebrow="Lineage-verified practice" lede="Guided and advanced sadhanas — each rooted in tradition, with mantra, ritual outline and timed practice." kind="sadhana" listPath="/sadhana" categoriesPath="/sadhana/categories" detailPath="/sadhana/:slug" showLevel />} />
                    <Route path="/sadhana/:slug" element={<DetailPage kind="sadhana" listPath="/sadhana" detailPath="/sadhana/:slug" listLabel="Sadhana" addLabel="Begin sadhana" />} />

                    <Route path="/products" element={<CatalogPage title="Sacred Products" eyebrow="Hand-selected, lineage-verified" lede="Yantras, malas, idols, ratnas, ritual items and books — sourced with care, prepared traditionally." kind="product" listPath="/products" categoriesPath="/products/categories" detailPath="/products/:slug" showRating />} />
                    <Route path="/products/:slug" element={<DetailPage kind="product" listPath="/products" detailPath="/products/:slug" listLabel="Products" />} />

                    <Route path="/services" element={<CatalogPage title="Personal Services" eyebrow="One-to-one consultation" lede="Astrology, vastu, numerology, palmistry, mantra dīkṣā and spiritual guidance — with senior practitioners." kind="service" listPath="/services" categoriesPath="/services/categories" detailPath="/services/:slug" />} />
                    <Route path="/services/:slug" element={<DetailPage kind="service" listPath="/services" detailPath="/services/:slug" listLabel="Services" />} />
                    <Route path="/services/booking/:slug" element={<BookingPage />} />

                    <Route path="/research" element={<CatalogPage title="Research Center" eyebrow="Peer-reviewed scholarship" lede="Original research at the intersection of consciousness studies, mantra science, iconography and comparative philosophy." kind="research" listPath="/research" categoriesPath="/research/categories" detailPath="/research/:slug" showPrice={false} showRating={false} />} />
                    <Route path="/research/:slug" element={<DetailPage kind="research" listPath="/research" detailPath="/research/:slug" listLabel="Research" />} />
                    <Route path="/research/:slug/read" element={<ReaderPage kind="research" />} />

                    <Route path="/library" element={<CatalogPage title="Digital Library" eyebrow="40,000-volume archive" lede="Classical texts, modern translations, rare commentaries — the digital backbone of NVL's scholarship." kind="book" listPath="/books" categoriesPath="/books/categories" detailPath="/library/:slug" showPrice={false} showRating={false} />} />
                    <Route path="/library/:slug" element={<DetailPage kind="book" listPath="/books" detailPath="/library/:slug" listLabel="Library" />} />
                    <Route path="/library/:slug/read" element={<ReaderPage kind="book" />} />

                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/videos" element={<VideosPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/order-success/:id" element={<OrderSuccessPage />} />
                    <Route path="/order-success" element={<OrderSuccessPage />} />

                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsConditionsPage />} />
                    <Route path="/refund-policy" element={<RefundPolicyPage />} />
                    <Route path="/accessibility" element={<AccessibilityPage />} />

                    <Route path="/maintenance" element={<MaintenancePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  <Route path="/dashboard/*" element={<RequireAuth><StudentDashboard /></RequireAuth>} />
                  <Route path="/admin/*" element={<RequireAdmin><AdminConsole /></RequireAdmin>} />
                </Routes>
              </ToastProvider>
            </TooltipProvider>
          </CartProvider>
        </AuthProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
}

export default App;
